import { rootApi } from '../rootApi';
import { baseQuery, baseQueryWithReauth } from '../base';

const toListResult = (response) => {
    if (response && response.data && Array.isArray(response.data.items)) {
        return { items: response.data.items, totalCount: response.data.totalCount || 0 };
    }
    return { items: [], totalCount: 0 };
};

export const lmsNewsApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getLmsActiveNews: builder.query({
            query: ({ search, planningLocation, trainingType, sortOrder, pageNumber, pageSize } = {}) => ({
                url: 'v1/LmsNews/GetActiveNews',
                method: 'POST',
                body: {
                    search: search || undefined,
                    planningLocation: planningLocation || undefined,
                    trainingType: trainingType || undefined,
                    sortOrder: sortOrder || 'desc',
                    pageNumber: pageNumber || 1,
                    pageSize: pageSize || 12
                }
            }),
            transformResponse: toListResult,
            providesTags: [{ type: 'LmsNews', id: 'LIST' }]
        }),

        getLmsAllNews: builder.query({
            query: ({ pageNumber, pageSize, filters, sorts } = {}) => ({
                url: 'v1/LmsNews/GetAllNews',
                method: 'POST',
                body: {
                    pageNumber: pageNumber || 1,
                    pageSize: pageSize || 10,
                    filters: Array.isArray(filters) ? filters : [],
                    sorts: Array.isArray(sorts) && sorts.length > 0 ? sorts : [{ column: 'PublishDate', direction: 2 }],
                    includeFilterOptions: false,
                    filterOptionsColumns: null
                }
            }),
            transformResponse: toListResult,
            providesTags: [{ type: 'LmsNews', id: 'LIST' }]
        }),

        getLmsNewsById: builder.query({
            query: (id) => ({
                url: 'v1/LmsNews/GetById',
                method: 'GET',
                params: { id }
            }),
            transformResponse: (response) => response?.data || null,
            providesTags: (result, error, id) => [{ type: 'LmsNews', id }]
        }),

        // 404 = planlamanın haberi yok; global hata toast'ı çıkmasın diye önce sessiz baseQuery denenir.
        getLmsNewsByPlanningId: builder.query({
            queryFn: async (planningId, api, extraOptions) => {
                const args = { url: 'v1/LmsNews/GetById', method: 'GET', params: { planningId } };
                const silent = await baseQuery(args, api, extraOptions);
                if (!silent.error) return { data: silent.data?.data || null };
                const status = silent.error.status === 'PARSING_ERROR' ? silent.error.originalStatus : silent.error.status;
                if (status === 404) return { data: null };
                const result = await baseQueryWithReauth(args, api, extraOptions);
                return result.error ? { error: result.error } : { data: result.data?.data || null };
            },
            providesTags: (result, error, planningId) => [{ type: 'LmsNews', id: `PLANNING-${planningId}` }, ...(result?.id ? [{ type: 'LmsNews', id: result.id }] : [])]
        }),

        // Blob yerine object URL saklanır, cache'ten düşünce revoke edilir.
        getLmsNewsPhoto: builder.query({
            queryFn: async ({ newsId }, api, extraOptions) => {
                const result = await baseQueryWithReauth(
                    {
                        url: 'v1/LmsNews/GetPhoto',
                        method: 'GET',
                        params: { newsId },
                        responseHandler: async (response) => (response.ok ? response.blob() : response.json().catch(() => null))
                    },
                    api,
                    extraOptions
                );
                if (result.error) return { error: result.error };
                return { data: URL.createObjectURL(result.data) };
            },
            async onCacheEntryAdded(arg, { cacheDataLoaded, cacheEntryRemoved }) {
                let url = null;
                try {
                    url = (await cacheDataLoaded).data;
                } catch {
                    return;
                }
                await cacheEntryRemoved;
                if (url) URL.revokeObjectURL(url);
            }
        }),

        addLmsNews: builder.mutation({
            query: (body) => ({
                url: 'v1/LmsNews/Add',
                method: 'POST',
                body
            }),
            invalidatesTags: [{ type: 'LmsNews', id: 'LIST' }]
        }),

        updateLmsNews: builder.mutation({
            query: (body) => ({
                url: 'v1/LmsNews/Update',
                method: 'POST',
                body
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'LmsNews', id: 'LIST' }, { type: 'LmsNews', id: arg?.id }]
        }),

        deleteLmsNews: builder.mutation({
            query: (id) => ({
                url: `v1/LmsNews/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['LmsNews']
        }),

        uploadLmsNewsPhoto: builder.mutation({
            query: ({ newsId, file }) => {
                const formData = new FormData();
                formData.append('NewsId', newsId);
                formData.append('Photo', file);
                return {
                    url: 'v1/LmsNews/UploadPhoto',
                    method: 'POST',
                    body: formData
                };
            },
            invalidatesTags: (result, error, arg) => [{ type: 'LmsNews', id: 'LIST' }, { type: 'LmsNews', id: arg?.newsId }]
        }),

        deleteLmsNewsPhoto: builder.mutation({
            query: (newsId) => ({
                url: 'v1/LmsNews/DeletePhoto',
                method: 'DELETE',
                params: { newsId }
            }),
            invalidatesTags: (result, error, newsId) => [{ type: 'LmsNews', id: 'LIST' }, { type: 'LmsNews', id: newsId }]
        })
    }),
    overrideExisting: false
});

export const {
    useGetLmsActiveNewsQuery,
    useGetLmsAllNewsQuery,
    useGetLmsNewsByIdQuery,
    useGetLmsNewsByPlanningIdQuery,
    useGetLmsNewsPhotoQuery,
    useAddLmsNewsMutation,
    useUpdateLmsNewsMutation,
    useDeleteLmsNewsMutation,
    useUploadLmsNewsPhotoMutation,
    useDeleteLmsNewsPhotoMutation
} = lmsNewsApi;
